import {
  type Components,
  createTheme,
  type PaletteOptions,
  type Theme,
} from '@mui/material/styles';
import type { TypographyOptions } from '@mui/material/styles/createTypography';

import type { ThemeConfig } from '@ayeldo/shared/types';

// Module augmentation for custom palette and shape properties
declare module '@mui/material/styles' {
  interface TypeBackground {
    subtle: string;
    overlay: string;
    surfaceLow: string;
    surfaceHigh: string;
    surfaceHighest: string;
  }

  interface TypeAction {
    selectedLight: string;
  }

  interface Palette {
    onSurfaceVariant: string;
    outlineVariant: string;
  }

  interface PaletteOptions {
    onSurfaceVariant?: string;
    outlineVariant?: string;
  }
}

interface PaletteInput {
  readonly appearance: ThemeConfig['appearance'];
  readonly effectivePrimary: string;
  readonly effectiveSecondary: string | undefined;
  readonly customColors: ThemeConfig['customColors'];
  readonly subtleBg: string;
  readonly overlayBg: string;
  readonly selectedLightBg: string;
  readonly surfaceLow: string;
  readonly surfaceHigh: string;
  readonly surfaceHighest: string;
  readonly onSurfaceVariant: string;
  readonly outlineVariant: string;
}

interface TypographyInput {
  readonly fontFamily: string;
  readonly headlineFontFamily: string;
}

interface ComponentOverridesInput {
  readonly borderRadius: number;
  readonly isLight: boolean;
  readonly fontFamily: string;
  readonly surfaceLow: string;
  readonly outlineVariant: string;
}

function buildPalette(input: PaletteInput): PaletteOptions {
  return {
    mode: input.appearance,
    primary: {
      main: input.effectivePrimary,
    },
    ...(input.effectiveSecondary !== undefined && input.effectiveSecondary !== ''
      ? {
          secondary: {
            main: input.effectiveSecondary,
          },
        }
      : {}),
    background: {
      ...(input.customColors?.background !== undefined && input.customColors.background !== ''
        ? { default: input.customColors.background }
        : {}),
      subtle: input.subtleBg,
      overlay: input.overlayBg,
      surfaceLow: input.surfaceLow,
      surfaceHigh: input.surfaceHigh,
      surfaceHighest: input.surfaceHighest,
    },
    ...(input.customColors?.text !== undefined && input.customColors.text !== ''
      ? {
          text: {
            primary: input.customColors.text,
          },
        }
      : {}),
    action: {
      selectedLight: input.selectedLightBg,
    },
    onSurfaceVariant: input.onSurfaceVariant,
    outlineVariant: input.outlineVariant,
  };
}

function buildTypography(input: TypographyInput): TypographyOptions {
  return {
    fontFamily: input.fontFamily,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontFamily: input.headlineFontFamily, fontWeight: 700 },
    h2: { fontFamily: input.headlineFontFamily, fontWeight: 700 },
    h3: { fontFamily: input.headlineFontFamily, fontWeight: 600 },
    h4: { fontFamily: input.headlineFontFamily, fontWeight: 600 },
    h5: { fontFamily: input.headlineFontFamily, fontWeight: 500 },
    h6: { fontFamily: input.headlineFontFamily, fontWeight: 500 },
    subtitle1: { fontFamily: input.headlineFontFamily },
    subtitle2: { fontFamily: input.headlineFontFamily },
    overline: { fontWeight: 600, letterSpacing: '0.05em' },
    button: { textTransform: 'none', fontWeight: 500 },
  };
}

function buildComponentOverrides(
  input: ComponentOverridesInput
): Components<Omit<Theme, 'components'>> {
  return {
    // ── Button ──────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius,
          paddingTop: 8,
          paddingBottom: 8,
        },
        sizeLarge: {
          paddingTop: 12,
          paddingBottom: 12,
        },
        sizeSmall: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },

    // ── IconButton ──────────────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius,
        },
      },
    },

    // ── Card ────────────────────────────────────────────────────────────────
    MuiCard: {
      defaultProps: {
        elevation: 0,
        variant: 'elevation',
      },
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius * 1.5,
          border: 'none',
          backgroundColor: input.isLight ? '#ffffff' : '#1e1e1e',
        },
      },
    },

    // ── Paper ────────────────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        variant: 'elevation',
      },
      styleOverrides: {
        rounded: {
          borderRadius: input.borderRadius * 1.5,
        },
      },
    },

    // ── TextField ────────────────────────────────────────────────────────────
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },

    // ── OutlinedInput ────────────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius,
          backgroundColor: input.surfaceLow,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${input.outlineVariant}40`, // 25% opacity
          },
          '&.Mui-focused': {
            backgroundColor: input.isLight ? '#ffffff' : '#121212',
          },
        },
        notchedOutline: {
          borderColor: `${input.outlineVariant}26`, // 15% opacity
        },
      },
    },

    // ── Chip ────────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius,
        },
      },
    },

    // ── AppBar ──────────────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          backgroundColor: input.isLight ? 'rgba(248,249,250,0.7)' : 'rgba(18,18,18,0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: 'none',
        },
      },
    },

    // ── Drawer ──────────────────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },

    // ── ListItemButton ───────────────────────────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius,
        },
      },
    },

    // ── Tooltip ──────────────────────────────────────────────────────────────
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },

    // ── Dialog ───────────────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: input.borderRadius * 1.5,
        },
      },
    },

    // ── Switch ───────────────────────────────────────────────────────────────
    ...(input.isLight
      ? {}
      : {
          MuiSwitch: {
            defaultProps: {
              color: 'secondary' as const,
            },
          },
        }),

    // ── Tabs ─────────────────────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 40,
        },
      },
    },

    // ── Skeleton ─────────────────────────────────────────────────────────────
    // Centralised borderRadius for rectangular skeletons so usage sites do not
    // need sx={{ borderRadius: 1 }} (see research.md D2).
    MuiSkeleton: {
      styleOverrides: {
        rectangular: {
          borderRadius: input.borderRadius,
        },
      },
    },

    // ── LinearProgress ────────────────────────────────────────────────────────
    // Centralised borderRadius so usage sites do not need sx={{ borderRadius: 1 }}
    // (see research.md D2).
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: input.borderRadius,
        },
      },
    },

    // ── CssBaseline ──────────────────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: input.fontFamily,
        },
      },
    },
  };
}

/**
 * Creates an Ayeldo MUI theme from a ThemeConfig.
 *
 * Styling hierarchy (enforced at the theme level):
 *   1. Component defaults defined here via `components` overrides — preferred for all shared styling.
 *   2. `styled()` from Emotion for component-specific styles not handled by the theme.
 *   3. `sx` prop only as a last resort for trivial one-off adjustments.
 *
 * @example Static app theme
 * ```typescript
 * import { createAyeldoTheme } from '@ayeldo/ui';
 *
 * export const STUDIO_THEME = createAyeldoTheme({
 *   appearance: 'light',
 *   primaryColor: '#000000',
 *   secondaryColor: '#12A594',
 *   headlineFont: "'Manrope', sans-serif",
 *   fontFamily: "'Inter', sans-serif",
 *   borderRadius: 8,
 * });
 * ```
 *
 * @example Dynamic tenant theme (portfolio)
 * ```typescript
 * import { createAyeldoTheme } from '@ayeldo/ui';
 * import { legacyRadixColorToHex, legacyRadixRadiusToPx } from '@ayeldo/shared';
 *
 * const theme = createAyeldoTheme({
 *   appearance: tenantSettings.appearance,
 *   primaryColor: legacyRadixColorToHex(tenantSettings.accentColor),
 *   borderRadius: legacyRadixRadiusToPx(tenantSettings.radius),
 * });
 * ```
 */
export function createAyeldoTheme(config: ThemeConfig): Theme {
  const {
    appearance,
    primaryColor,
    secondaryColor,
    borderRadius = 8,
    fontFamily = "'Be Vietnam Pro', sans-serif",
    headlineFont,
    customColors,
    surfaceColors,
  } = config;

  const rawPrimary = customColors?.primary ?? primaryColor;
  const isLight = appearance === 'light';
  const effectivePrimary = !isLight && isVeryDark(rawPrimary) ? '#ffffff' : rawPrimary;
  const effectiveSecondary = customColors?.secondary ?? secondaryColor;
  const subtleBg = isLight ? '#FAFAFA' : '#1A1A1A';
  const overlayBg = isLight ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.85)';
  const selectedLightBg = `${effectivePrimary}14`; // ~8% alpha

  // Surface scale defaults derived from appearance
  const surfaceLow = surfaceColors?.surfaceLow ?? (isLight ? '#f3f4f5' : '#1a1c1d');
  const surfaceHigh = surfaceColors?.surfaceHigh ?? (isLight ? '#e8eaeb' : '#2a2c2d');
  const surfaceHighest = surfaceColors?.surfaceHighest ?? (isLight ? '#e1e3e4' : '#3a3c3d');
  const onSurfaceVariant = surfaceColors?.onSurfaceVariant ?? (isLight ? '#444748' : '#c4c7c7');
  const outlineVariant = surfaceColors?.outlineVariant ?? (isLight ? '#c4c7c7' : '#444748');

  // Dual-font typography: headline font for display/heading variants, body font for everything else
  const headlineFontFamily = headlineFont ?? fontFamily;

  return createTheme({
    palette: buildPalette({
      appearance,
      effectivePrimary,
      effectiveSecondary,
      customColors,
      subtleBg,
      overlayBg,
      selectedLightBg,
      surfaceLow,
      surfaceHigh,
      surfaceHighest,
      onSurfaceVariant,
      outlineVariant,
    }),

    typography: buildTypography({ fontFamily, headlineFontFamily }),

    shape: {
      borderRadius,
    },

    components: buildComponentOverrides({
      borderRadius,
      isLight,
      fontFamily,
      surfaceLow,
      outlineVariant,
    }),
  });
}

/**
 * Returns true if a hex color has very low luminance (< 0.05),
 * meaning it would be invisible against a dark background.
 *
 * Only accepts 6-digit hex colors (e.g. `#rrggbb`). Returns false for
 * short-form (`#rgb`) or non-hex input.
 */
function isVeryDark(hex: string): boolean {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return false;
  }
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.05;
}
